import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { Box, CircularProgress, Grid, IconButton, MenuItem, Typography, DialogActions, Button, SwipeableDrawer, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useTourBookingContext } from '@/containers/TourBookingByCustomer/providers/useTourBookingContext';
import { emailPattern, formatDateDisplay, formatTimeDisplay, polylineCoords } from '@/utils/utils';
import { IDialCode, ITourBookingCustomerForm, ITourBookingForm } from '@/types/interface';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import useStoreApp from '@/store';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { tourBookingCustomerSchema } from '@/types/tourBookingSchema';
import useTourBooking from '@/hooks/useTourBooking';
import { bookingService } from '@/services/bookingService';
import { formatPriceWithNoUnit } from '@/utils/formatPrice';
import InputAutocompleteClass from '@/components/InputAutocompleteClass';
import { DepartureIcon, OpenMapIcon } from '@/components/SvgIcons';
import { formatNumber } from '@/utils/handleFormatNumber';
import PreLoading from '@/components/PreLoading';
import PassengersLimitModal from '@/components/PassengersLimitModal';
import MapDialog from '@/components/MapDialog';
import {
  BookingFormCard,
  BookingFormSection,
  BookingFormInput,
  BookingFormSelect,
  BookingFormButton,
  BookingFormPromoInput,
  BookingFormPicker,
  TourPriceOptionsSection,
  AdditionalServicesSection,
  TourTimeline,
  SummarizationTourInfo,
} from '@/components/BookingFormCustom';
import { BookingForm } from './style';
import { ArrowForward } from '@mui/icons-material';
import { hoiangoShowLog } from '@/utils/log';
import { ContactNumberType } from '@/types/consistency';
import { parsePhoneNumber } from 'libphonenumber-js';
import { trackTourBookingButtonClick } from '@/utils/gtag';
import { TimePickerCustom } from '@/components/DateTimeCustom';
import useEmbed, { PostMessageType } from '@/hooks/useEmbed';

type BookingFormPanelProps = {
  onSubmitBooking: (formData: ITourBookingForm) => void;
  onSummaryVisibilityChange?: (isVisible: boolean) => void;
};

const BookingFormPanel: React.FC<BookingFormPanelProps> = ({ onSubmitBooking, onSummaryVisibilityChange }) => {
  const router = useRouter();
  const summaryRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();

  const { t } = useTranslation(['validate', 'common']);

  const { user, dialCodes, isEmbeed } = useStoreApp(
    state => state.globalSlice,
  );

  const {
    setBookingTourData,
    durationOptions,
    services,
    selectedTour,
    // totalAmount,
    setContactInfo,
    storePromotion,
    setStorePromotion,
    tourDetail,
    computedPricing,
    isFetchingTourDetail,
    isFetchingTours,
    selectedTourIdForBooking,
  } = useTourBookingContext();

  const { sendPostMessage } = useEmbed();

  const [typeMap, setTypeMap] = useState<'' | 'pickUp' | 'dropOff'>('');
  const [pickUpMapValue, setPickUpMapValue] = useState<string | null>(
    router.query.address as string | null,
  );

  const [collaboratorId] = useState<string | null>(
    router.query.collaboratorId as string | null,
  );

  const [promotionName] = useState<string | null>(
    router.query.promotionName as string | null,
  );

  const haveRouterQuery = router.query.address && router.query.lat && router.query.lng;

  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [locationErr, setLocatitonErr] = useState<string | undefined>(
    undefined,
  )
  const [showPassengersModal, setShowPassengersModal] =
    useState<boolean>(false);

  const [showDialCode, setShowDialCode] = useState<boolean>(false);

  // Mobile timeline drawer state
  const [showTimelineDrawer, setShowTimelineDrawer] = useState<boolean>(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // CustomActionBar removed as per user request (Go Now logic not applicable here)

  // Move useTourBooking hook up here so its values are available for schema and useForm
  const {
    pickUpData,
    setPickUpData,
    date,
    setDate,
    dateErrMsg,
    time,
    timeErrMsg,
    setDateTimeErrMsg,
    checkPickUpAndDropOff,
    minDate,
    setTimeIdSelected,
    setTime,
    setIsChangedTimer,
    isCurrentDate,
    contactNumberTypeOptions,
    handlePromotion,
    promotion,
    setPromotion,
    errorPromotion,
    setErrorPromotion,
    // reComputePrice,
    // getDiscountPrice
    validateBookingTime,
    validateTourWorkingTime,
    minTime,
    maxTime,
  } = useTourBooking({
    service: bookingService,
    tourType: selectedTour?.tourType,
    currentTour: selectedTour,
  });

  const schema = useMemo(
    () =>
      yup
        .object()
        .shape({
          ...tourBookingCustomerSchema,
          dialCode: yup.string(),
          timeIdSelected: yup
            .number()
            .typeError('required')
            .required('required')
            .test(
              'validate-tour-working-time',
              'validate tour working time',
              function (value: any) {

                //hoiangoShowLog({
                //  title: 'validate-tour-working-time on call',
                //  value: `${ value } `,
                //});

                if (value) {
                  //const formValues = this.parent;

                  const msg = validateTourWorkingTime(selectedTour, value);

                  //hoiangoShowLog({
                  //  title: 'validate-tour-working-time on call',
                  //  value: `current value ${value} \nerror message: ${msg} `,
                  //});

                  if (msg) {
                    return this.createError({
                      path: 'timeIdSelected',
                      message: msg,
                    });
                  }
                }
                return true;
              }
            ),
          email: yup
            .string()
            .trim()
            .nullable()
            .optional()
            .test("check-parent-email", "emailInvalid", function (value: any) {
              const email = value;

              const validEmail = emailPattern.test(email)

              if (validEmail) {
                return true;
              }
              else if (!email || email.trim() === '') {
                return this.createError({
                  path: 'email',
                  message: 'emailRequired'
                });
              }

              return this.createError({
                path: 'email',
                message: 'emailInvalid'
              });
            })

        })
        .test(
          'at-least-one',
          'contactNumber is required',
          function (value) {
            const { contactNumber } = value;
            const trimmedContactNumber = contactNumber?.trim();

            if (trimmedContactNumber) {
              clearErrors(["contactNumber"]);
              return true;
            }

            if (!trimmedContactNumber) {
              const errors = [
                this.createError({
                  path: 'contactNumber',
                  message: 'required'
                })
              ];
              return new yup.ValidationError(errors);
            }

            return true;

          },
        ),
    [selectedTour, selectedTour?.startActiveTime, selectedTour?.endActiveTime, validateTourWorkingTime, collaboratorId]
  );

  const {
    clearErrors,
    setValue,
    handleSubmit,
    register,
    getValues,
    watch,
    trigger,
    formState: { errors, isDirty, isValid },
  } = useForm<ITourBookingCustomerForm>({
    mode: 'all',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (durationOptions && durationOptions.length > 0) {
      setValue('tourPriceOptionId', durationOptions[0].id);
    }

    return () => {
      setValue('tourPriceOptionId', '');
    }

  }, [durationOptions, setValue]);

  useEffect(() => {
    // Sync timeIdSelected from time whenever time changes
    if (time) {
      const hour = time.hour();
      const minute = time.minute();
      const timeId = hour * 100 + minute;

      const currentVal = getValues('timeIdSelected');
      if (currentVal !== timeId) {
        setValue('timeIdSelected', timeId, { shouldValidate: true });
      }
    }
  }, [time, setValue, getValues]);


  const formValues = watch();

  // Get the selected tour price option based on the selected duration
  const selectedTourPriceOption = useMemo(() => {
    if (!selectedTour?.tourPriceOptions || !formValues?.tourPriceOptionId) return null;
    return selectedTour.tourPriceOptions.find(opt => opt.id === formValues.tourPriceOptionId) || selectedTour.tourPriceOptions[0] || null;
  }, [selectedTour?.tourPriceOptions, formValues?.tourPriceOptionId]);

  useEffect(() => {
    checkPickUpAndDropOff(setValue, clearErrors);
  }, [pickUpData]);

  useEffect(() => {
    setBookingTourData(prevData => ({
      ...prevData,
      tourPriceOptionId: selectedTourPriceOption?.id ?? '',
      date: date,
      time: time,
      pickUpAddress: pickUpData?.pickUpAddress ?? '',
      pickUp: pickUpData?.pickUp ? JSON.parse(pickUpData?.pickUp) : undefined,
      passengerNormal: formValues.adults ? Number(formValues.adults) : 0,
      // passengerHalf: formValues.children ? Number(formValues.children) : 0,
      note: formValues.note,
      additionalServiceIds: selectedServiceIds,
      promotionDetailId: !!promotion ? promotion.promotionDetailId : undefined,
      promotionId: !!promotion ? promotion.promotionId : undefined,
    }));
  }, [
    date,
    formValues.adults,
    formValues.tourPriceOptionId,
    formValues.note,
    pickUpData,
    selectedServiceIds,
    setBookingTourData,
    time,
    promotion
  ]);

  const tel = useMemo(() => {
    if (watch('phone') && watch('dialCode')) {
      return parsePhoneNumber(watch('phone'), watch('dialCode'))?.number;
    }
    return undefined;
  }, [watch('phone'), watch('dialCode')]);

  const countryCode = useMemo(() => {
    if (dialCodes.length <= 0) return undefined;
    const selectedDialCode = watch('dialCode');
    const foundDialCode = dialCodes.find((e: IDialCode) => e.code === selectedDialCode);
    return foundDialCode;
  }, [watch('dialCode'), dialCodes.length]);

  useEffect(() => {
    formValues.phone = '';
    formValues.zalo = '';
    formValues.whatsapp = '';
    formValues.kakaotalk = '';
    formValues.viber = '';

    switch (formValues.contactNumberType) {
      case 'phone':
        formValues.phone = formValues.contactNumber;
        break;
      case 'zalo':
        formValues.zalo = formValues.contactNumber;
        break;
      case 'whatsapp':
        formValues.whatsapp = formValues.contactNumber;
        break;
      case 'kakaotalk':
        formValues.kakaotalk = formValues.contactNumber;
        break;
      case 'viber':
        formValues.viber = formValues.contactNumber;
        break;
      default:
        formValues.phone = formValues.contactNumber;
        break;
    }

    setContactInfo(prevData => ({
      ...prevData,
      fullName: formValues.fullName,
      phone: formValues.phone,
      email: formValues.email,
      zalo: formValues.zalo,
      whatsapp: formValues.whatsapp,
      kakaotalk: formValues.kakaotalk,
      viber: formValues.viber,
      dialCode: countryCode?.dialCode,
    }));
  }, [
    formValues.fullName,
    formValues.contactNumberType,
    formValues.contactNumber,
    formValues.email,
    formValues.dialCode,
    setContactInfo,
  ]);

  useEffect(() => {
    if (haveRouterQuery) {
      setPickUpData({
        pickUp: JSON.stringify({
          lat: Number(router.query.lat),
          lng: Number(router.query.lng),
        }),
        pickUpAddress: router.query.address as string,
      });
    }

    return () => {
      setPickUpData(null);
    }
  }, [router, setPickUpData]);

  useEffect(() => {
    if (haveRouterQuery) {
      return;
    }

    if (selectedTour?.pickUp && selectedTour?.pickUpAddress) {
      setPickUpData({
        pickUp: selectedTour.pickUp,
        pickUpAddress: selectedTour.pickUpAddress,
      });
    }

    return () => {
      setPickUpData(null);
    }
  }, [tourDetail, selectedTour]);


  useEffect(() => {
    const contactNumberType = watch('contactNumberType');

    let needDialCode = contactNumberType == ContactNumberType.Zalo.code || contactNumberType == ContactNumberType.Whatsapp.code;

    if (needDialCode !== showDialCode) {
      setShowDialCode(needDialCode);
    }

  }, [watch('contactNumberType')]);

  // IntersectionObserver to detect SummarizationTourInfo visibility on mobile
  useEffect(() => {
    if (!isMobile || !onSummaryVisibilityChange) return;

    const ref = summaryRef.current;
    if (!ref) return;

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        onSummaryVisibilityChange(entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0.1,
      }
    );

    observer.observe(ref);

    return () => {
      observer.disconnect();
    };
  }, [isMobile, onSummaryVisibilityChange]);

  const handleSelectService = (value: boolean, id: string) => {
    //hoiangoShowLog({
    //  title: 'handleSelectService',
    //  value: `\nvalue: ${ value } \nid: ${ id } `,
    //});

    if (value) {
      setSelectedServiceIds(prevData => [...prevData, id]);
      return;
    }
    setSelectedServiceIds(prevData => prevData.filter(p => p !== id));
  };

  // validate location
  useEffect(() => {
    setLocatitonErr(undefined);

    try {
      if (pickUpData && pickUpData !== null) {
        const polyline = new (window as any).google.maps.Polygon({
          paths: polylineCoords,
        });
        const pickUp = JSON.parse(pickUpData.pickUp ?? '');
        const pickUpLatLng = new (window as any).google.maps.LatLng(
          pickUp.lat,
          pickUp.lng,
        );

        if (
          !(window as any).google.maps.geometry.poly.containsLocation(
            pickUpLatLng,
            polyline,
          )
        ) {
          setLocatitonErr(t('validate:locationIsOutside') ?? '');
        }
      }
    } catch (error) {
      console.error('Error in useEffect', error);
    }

    return () => {
      setLocatitonErr(undefined);
    }


  }, [pickUpData]);

  const onSubmit = useCallback(
    (formData: ITourBookingForm) => {
      if (selectedTour === null) return;
      if (locationErr) return;

      if (Number(formData.adults) > selectedTour.passengerLimit) {
        setShowPassengersModal(true);
        return;
      }

      validateBookingTime();

      if (dateErrMsg.length > 0 || timeErrMsg.length > 0) return;

      if (storePromotion === undefined || storePromotion === null) {
        setStorePromotion(promotion);
      }

      onSubmitBooking(formData);
      trackTourBookingButtonClick('book_tour_now', selectedTour.id, 'click_book_tour_now');
    },
    [selectedTour, dateErrMsg, timeErrMsg, pickUpData, onSubmitBooking, t],
  );

  useEffect(() => {
    setTimeIdSelected(formValues.timeIdSelected);

    return () => {
      setTimeIdSelected(undefined);
    }
  }, [formValues.timeIdSelected]);

  useEffect(() => {
    // if user is logged in, don't handle promotion
    if (!!user) return;

    //console.group('TourBookingForm')
    //console.log('promotion', promotion)
    //console.groupEnd()

    // if (!!promotion) {
    //   reComputePrice(totalAmount);
    // }
    setStorePromotion(promotion)
  }, [promotion, setStorePromotion]);
  // }, [totalAmount, promotion, setStorePromotion, reComputePrice]);


  useEffect(() => {

    // if user is logged in, don't handle promotion
    if (!!user) return;

    if (promotionName !== undefined && promotionName !== null && promotionName !== '' && collaboratorId !== null && collaboratorId !== '') {
      handlePromotion(promotionName, collaboratorId);
    }
  }, [promotionName, collaboratorId]);



  if (/*selectedTourIdForBooking === null ||*/ isFetchingTours || selectedTour === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }


  return (
    <>
      <BookingFormCard
        as={BookingForm}
        onSubmit={handleSubmit(onSubmit, (errors) => {
          console.group('handleSubmit -> errors');
          console.log('errors:', errors);
          console.groupEnd();
        })}

        className={selectedTour === null ? 'loading' : ''}
      >
        {selectedTour !== null && selectedTour ? (
          <>
            {/* Tour Info Section */}
            <BookingFormSection>

              <Typography variant="h4" fontWeight={700} mb={{
                md: theme.spacing(2),
                xs: theme.spacing(2)
              }}>{t('common:choose_tour_package_and_timeline')}</Typography>

              {/* Tour Route Selection - Card Layout */}
              <TourPriceOptionsSection
                tourPriceOptions={selectedTour?.tourPriceOptions || []}
                selectedTourPriceOptionId={formValues.tourPriceOptionId}
                onSelectTourPriceOptionId={(duration) => {
                  setValue('tourPriceOptionId', duration);
                  trigger('tourPriceOptionId');
                }}
                onOpenMobileDrawer={() => {

                  if (isEmbeed) {
                    sendPostMessage(PostMessageType.present_visit_places_data,
                      {
                        title: t('common:service_package_details'),
                        scheduleTitle: t('common:schedule'),
                        tourPriceOption: selectedTourPriceOption
                      }
                    )
                    return;
                  }

                  setShowTimelineDrawer(true)
                }}
              />

              {/* Additional Services Section */}
              {services.length > 0 ? <AdditionalServicesSection
                services={services}
                selectedServiceIds={selectedServiceIds}
                onSelectService={handleSelectService}
              /> : null}

              {(timeErrMsg || dateErrMsg) && (
                <Typography color="error" fontSize={13} mt={2}>
                  {timeErrMsg === '' ? t(dateErrMsg) : timeErrMsg}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                <BookingFormPicker
                  label={
                    <Typography component="span" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                      {t('common:selectDate')}
                      <Typography color={t => t.palette.error.main} component="span">
                        {' *'}</Typography>
                    </Typography>
                  }
                  value={date}
                  onChange={(val: any) => {
                    setDate(val);
                  }}
                  format={formatDateDisplay}
                  minDate={minDate}
                  disablePast
                  sx={{ flex: 1 }}
                />
                <Box sx={{ display: 'flex', flex: { xs: 1, md: 2, lg: 2 }, gap: 2, marginBottom: "0 !important" }}>
                  <TimePickerCustom
                    label={
                      <Typography component="span" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                        {t('common:selectTime')}
                        <Typography color={t => t.palette.error.main} component="span">
                          {' *'}</Typography>
                      </Typography>
                    }
                    variant="filled"
                    ampm={false}
                    ampmInClock={false}
                    format={formatTimeDisplay}
                    value={time}
                    minTime={minTime}
                    maxTime={maxTime}
                    {...register('timeIdSelected')}
                    onChange={(val: any, ctx: any) => {
                      setIsChangedTimer(true);
                      setDateTimeErrMsg(ctx.validationError, 'time');
                      setTime(val);

                      if (val) {
                        const hour = val.hour();
                        const minute = val.minute();
                        const timeId = hour * 100 + minute;
                        setValue('timeIdSelected', timeId);
                        trigger('timeIdSelected');
                      }
                    }}
                    disablePast={isCurrentDate}
                    error={!!timeErrMsg}
                    sx={{ flex: 1 }}
                  />
                  <BookingFormInput
                    variant="filled"
                    label={
                      <Typography
                        component="span"
                        sx={{ fontSize: { xs: 12, sm: 14 } }}>
                        {t('common:adultGreaterThan2')}
                        <Typography color={t => t.palette.error.main} component="span">
                          {' *'}</Typography>
                      </Typography>
                    }
                    type="number"
                    inputProps={{ inputMode: 'numeric' }}
                    autoComplete="off"
                    register={register('adults')}
                    error={Boolean(errors.adults)}
                    helperText={errors.adults?.message ? t(errors.adults?.message) : ' '}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start', paddingTop: 0 }}>
                {isFetchingTourDetail ?
                  <CircularProgress size={24} /> :
                  (
                    <InputAutocompleteClass
                      variant="filled"
                      //isInitDefaultLocation={!isFetchingTourDetail && (!tourDetail || (!tourDetail?.pickUp && !tourDetail?.pickUpAddress)) && !router.query.address}
                      isInitDefaultLocation={true}
                      tourPickUp={pickUpData?.pickUp}
                      tourPickUpAddress={pickUpData?.pickUpAddress}
                      isReset={false}
                      idText="pickUp"
                      label={<span>{t('common:pickupLocation')}<Typography color={t => t.palette.error.main} component="span"> *</Typography></span>}
                      icon={
                        <IconButton onClick={() => setTypeMap('pickUp')} size="small">
                          <OpenMapIcon />
                        </IconButton>
                      }
                      setOutput={setPickUpData}
                      setValue={(val: string, options?: any) => setValue('pickUp', val, { ...options })}
                      mapValue={pickUpMapValue}
                      register={register('pickUp')}
                      error={Boolean(errors.pickUp) || Boolean(locationErr)}
                      helperText={locationErr ? locationErr : errors.pickUp?.message ? t(errors.pickUp?.message) : ''}
                      sx={{ flex: 1 }}
                    />
                  )
                }
              </Box>
            </BookingFormSection>

            {/* Visit Place Ticket Fees */}
            {/*<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, pb: 3, borderBottom: '1px solid #c4c4c4' }}>
              <Typography variant="h3" fontWeight={700} flex={1}>{t('common:visitPlaceTicketFees')}</Typography>
              <Typography variant="h4" fontWeight={700}>{formatPriceWithNoUnit(computedPricing?.visitPlaceTicketFee)} VNĐ</Typography>
            </Box>*/}

            <Box sx={{
              display: { xs: 'row', md: 'flex' },
              gap: { xs: 0, md: 4 },
              marginBottom: { xs: 2, md: 0.75 },
            }}>
              {/* Promotion Code Section */}
              {!user && (
                <BookingFormSection title={t('common:promotionCode')} sx={{ flex: 1, }}>
                  <BookingFormPromoInput
                    value={promotionName ?? watch('promotionCode') ?? ''}
                    onChange={e => setValue('promotionCode', e.target.value)}
                    onApply={() => {
                      const value = watch('promotionCode');
                      if (value && value.trim()) {
                        handlePromotion(value);
                      } else {
                        setPromotion(null);
                        setErrorPromotion('');
                      }
                    }}
                    // TODO: Re-check here
                    disabled={promotionName !== undefined && promotionName !== null && promotionName !== ''}
                    error={Boolean(errorPromotion)}
                    helperText={!!promotion ? `${t('common:discount')}: ${formatNumber(computedPricing?.discount)} VNĐ` : errorPromotion ?? undefined}
                    sx={{
                      mt: {
                        xs: 0,
                        md: 3
                      }
                    }}
                  />
                </BookingFormSection>
              )}

              {/* Notes Section */}
              <BookingFormSection title={t('common:notes')} sx={{ flex: 1 }}>
                <BookingFormInput
                  autoComplete="off"
                  placeholder={t('common:description')!}
                  register={register('note')}
                  multiline
                  rows={2}
                  sx={{
                    textarea: { height: '36px !important' }
                  }}
                  inputProps={{ maxLength: 256, inputMode: 'text' }}
                  fullWidth
                />
              </BookingFormSection>

            </Box>

            {/* Passenger Info Section */}
            <BookingFormSection title={t('common:passengerInfo')}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 1, md: 2 },
                flexWrap: 'wrap',
                pb: { xs: 0, md: 2 },
              }}>
                <Box flex={1}>
                  <BookingFormInput
                    size="small"
                    variant="filled"
                    label={t('common:fullName')}
                    required={true}
                    fullWidth
                    register={register('fullName')}
                    inputProps={{ maxLength: 512, inputMode: 'text' }}
                    error={Boolean(errors.fullName)}
                    helperText={errors.fullName?.message ? t(errors.fullName?.message) : ''}

                  />
                </Box>
                <Box flex={1}>
                  <BookingFormInput
                    size="small"
                    variant="filled"
                    label="Email"
                    fullWidth
                    type='email'
                    required={true}
                    register={register('email')}
                    error={Boolean(errors.email)}
                    inputProps={{ maxLength: 128, inputMode: 'email' }}
                    helperText={errors.email?.message ? t(errors.email?.message) : ''}
                    xs={{ flex: 1 }}
                  />
                </Box>
                <BookingFormSelect
                  key={selectedTour.id}
                  label={t('common:contactInformation')}
                  options={contactNumberTypeOptions}
                  defaultValue={contactNumberTypeOptions[0].value}
                  {...register('contactNumberType')}
                  required={true}
                  sx={{
                    flex: {
                      xs: 1,
                      md: 1,
                    },
                    gap: {
                      xs: 1,
                      md: 0,
                    },
                    py: {
                      xs: 1,
                      md: 0,
                    }
                  }}
                />
                {
                  showDialCode ? (
                    <BookingFormSelect
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: '300px',
                          },
                        },
                      }}
                      sx={{
                        marginRight: 1,
                        flex: 1,
                        py: {
                          xs: 1,
                          md: 0,
                        }
                      }}
                      defaultValue="VN"
                      label={t('common:dialCode')}
                      {...register('dialCode')}
                      options={dialCodes.map((e: IDialCode) => ({
                        label: e.name,
                        value: e.code,
                      }))}
                      onChange={(e: any) => {
                        register('dialCode').onChange(e);
                      }}
                    >
                      {dialCodes.map((e: IDialCode, i: number) => (
                        <MenuItem value={e.code} key={i}>
                          {e.name}
                        </MenuItem>
                      ))}
                    </BookingFormSelect>
                  ) : null
                }
                <BookingFormInput
                  size="small"
                  variant="filled"
                  label={t('common:contactInformationInput')}
                  register={register('contactNumber')}
                  required={true}
                  error={Boolean(errors.contactNumber)}
                  inputProps={{ maxLength: 20, inputMode: 'text' }}
                  helperText={errors.contactNumber?.message ? t(errors.contactNumber?.message) : ''}
                  sx={{
                    flex: 1,
                    my: {
                      xs: 1,
                      md: 0,
                    }
                  }}
                />
              </Box>
            </BookingFormSection>

            {/* Total and Book Now Button Section - Desktop Only */}
            <Box
              ref={summaryRef}
              sx={{
                display: {
                  xs: isEmbeed ? "block" : 'none',
                  md: 'block'
                },
              }}
            >
              <SummarizationTourInfo
                promotion={promotion}
                computedPricing={computedPricing}
                isDisableSubmit={!isDirty || !isValid}
                sxContainer={{
                  backgroundColor: 'transparent',
                  ml: '0',
                  width: '100%',
                  padding: '0.75rem 0',
                }}
                sxLeft={{
                  alignItems: {
                    xs: 'flex-start',
                    md: 'flex-start',
                  },
                  pl: {
                    xs: '0.75rem',
                    md: 0
                  }
                }}
                toolTipPosition="behindTotal"
                summaryType='customer'
              />
            </Box>

            {/*<Box sx={{ background: '#fff', borderRadius: 12, padding: 16, marginTop: 32 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography fontWeight={600} fontSize={18}>{t('common:totalAmount')}</Typography>
                <Typography fontWeight={700} fontSize={22} color={theme => theme.palette.primary.main}>
                  {formatPriceWithNoUnit(computedPricing?.money)} VNĐ
                </Typography>
              </Box>
              <BookingFormButton
                type="submit"
                sx={{
                  borderRadius: isEmbeed ? "6px" : "24px",
                  background: theme => theme.palette.primary.main,
                  '&:hover': {
                    background: theme => alpha(theme.palette.primary.main, 0.75),
                  },
                  '&:disabled': {
                    background: theme => theme.palette.grey[300],
                  },
                }}
                disabled={!isDirty || !isValid}
              >
                <ArrowForward sx={{ fontSize: 24, mr: 1, display: { xs: 'none', sm: 'block' } }} />
                {t('common:bookTourNow').toUpperCase()}
              </BookingFormButton>
            </Box>*/}
          </>
        ) : (
          <PreLoading />
        )}
      </BookingFormCard >

      <PassengersLimitModal
        isOpen={showPassengersModal}
        yesAction={() => setShowPassengersModal(false)}
        maxLimit={selectedTour?.passengerLimit ?? 0}
      />

      <MapDialog
        type={typeMap}
        onClose={() => setTypeMap('')}
        setMapValue={value => {
          if (typeMap === 'pickUp') setPickUpMapValue(value);
        }}
        pickUpData={pickUpData}
        setPickUpData={data => {
          setPickUpData(data);
        }}
      />

      {/* Mobile Timeline Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={showTimelineDrawer}
        onClose={() => { }} // Disable swipe to close - only close button works
        onOpen={() => { }}
        disableSwipeToOpen
        disableDiscovery
        swipeAreaWidth={0} // Disable swipe area
        PaperProps={{
          sx: {
            maxHeight: '85dvh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          }
        }}
      >
        {/* Header with title and close button */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          zIndex: 1,
        }}>
          <Typography variant="h6" fontWeight={600}>
            {t('common:service_package_details')}
          </Typography>
          <IconButton
            onClick={() => setShowTimelineDrawer(false)}
            size="small"
            sx={{
              backgroundColor: 'grey.100',
              '&:hover': { backgroundColor: 'grey.200' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable content */}
        <Box sx={{
          overflowY: 'auto',
          p: 0,
          backgroundColor: 'grey.50',
        }}>
          <TourTimeline selectedTourPriceOption={selectedTourPriceOption} isBottomSheet />
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default BookingFormPanel; 